import React, { useState, useEffect, useCallback } from "react";
import * as S from "./style";
import { Package, TrendingUp, Download } from "lucide-react";
import Table from "@components/table";
import { Reports, Zone, Store } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import { useModal } from "@context/ModalContext";
import CreateProductForm from "@commons/store-setup/individual-store/product-setup/form/Main";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

import { useNavigate } from "react-router-dom";
import { APP_BASE_URL, API_KEY } from "@utils/request";
import { toast } from "react-toastify";

const ItemReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [summary, setSummary] = useState({
    totalUnitsSold: 0,
    totalRevenue: 0,
    totalItems: 0,
  });
  const { settings: currencySettings } = useGeneralSettings();
  const { bucketUrl } = useConfig();
  const { showModal } = useModal();

  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: zones } = useSelector((state: RootState) => state.zones);
  const { items: stores } = useSelector((state: RootState) => state.stores) as any;

  useEffect(() => {
    Zone.list({ state: "zones" });
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    moduleId: "",
    zoneId: "",
    storeId: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await Reports.getItemReport({
        params: {
          page,
          limit: 10,
          ...filters,
        },
      });
      if (res) {
        setData(res.data || []);
        setTotalItems(res.meta?.total || 0);
        setTotalPages(res.meta?.totalPages || 0);
        setSummary({
          totalUnitsSold: res.summary?.totalUnitsSold || 0,
          totalRevenue: res.summary?.totalRevenue || 0,
          totalItems: res.summary?.totalItems || res.meta?.total || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch items:", error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Stores are scoped to the selected module: only fetch them once a module is
  // chosen. While no module is selected the Store dropdown stays disabled.
  useEffect(() => {
    if (filters.moduleId) {
      Store.list({
        state: "stores",
        params: { limit: 1000, moduleId: filters.moduleId },
      });
    }
  }, [filters.moduleId]);

  const handleFilterChange = (key: string, value: string) => {
    let updates: any = { [key]: value };
    // Changing the module invalidates the current store selection.
    if (key === "moduleId") {
      updates.storeId = "";
    }
    if (key === "dateRange") {
      const today = new Date();
      if (value === "today") {
        updates.startDate = today.toISOString().split("T")[0];
        updates.endDate = updates.startDate;
      } else if (value === "this_week") {
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_month") {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_year") {
        const firstDay = new Date(today.getFullYear(), 0, 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "custom") {
        // preserve current dates, let user pick
      } else {
        updates.startDate = "";
        updates.endDate = "";
      }
    }
    setFilters(prev => ({ ...prev, ...updates }));
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    const toastId = toast.info("Generating Excel report, please wait...", { autoClose: false });
    try {
      const user = Helper.getUser();
      const token = user?.token;
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.moduleId) queryParams.append("moduleId", filters.moduleId);
      if (filters.zoneId) queryParams.append("zoneId", filters.zoneId);
      if (filters.storeId) queryParams.append("storeId", filters.storeId);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);
      
      const downloadUrl = `${APP_BASE_URL}/admin/reports/items/export?${queryParams.toString()}`;
      
      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Api-Key": API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export Excel report");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `item_sales_report_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.update(toastId, {
        render: "Excel report exported successfully!",
        type: "success",
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.update(toastId, {
        render: error?.message || "Failed to export Excel report",
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  const stats = [
    {
      label: "Total Items Sold",
      value: `${summary.totalUnitsSold.toLocaleString()} Units`,
      Icon: Package,
      color: "#1890ff",
      bgColor: "#e6f7ff",
    },
    {
      label: "Report Total Items",
      value: `${summary.totalItems.toLocaleString()}`,
      Icon: TrendingUp,
      color: "#52c41a",
      bgColor: "#f6ffed",
    },
    {
      label: "Total Revenue",
      value: Helper.formatCurrency(summary.totalRevenue, currencySettings),
      Icon: TrendingUp,
      color: "#faad14",
      bgColor: "#fffbe6",
    },
  ];

  const columns = [
    {
      title: "Item Name",
      key: "name",
      render: (item: any) => {
        const imageUrl = item.image ? `${bucketUrl}/${item.image}` : null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.name}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: "#e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#64748b",
                }}
              >
                {item.name ? item.name.charAt(0).toUpperCase() : "?"}
              </div>
            )}
            <span style={{ fontWeight: 500 }}>{item.name}</span>
          </div>
        );
      },
    },
    { title: "Category", key: "category" },
    { 
      title: "Store", 
      key: "store",
      render: (item: any) => {
        const storeImg = item.storeAvatar ? `${bucketUrl}/${item.storeAvatar}` : null;
        return (
          <S.ProfileCard 
            onClick={() => navigate(`/admin/users/store/store-lists/view/${item.storeId}`)}
            style={{ cursor: "pointer" }}
          >
            {storeImg ? (
              <S.ProfileAvatar 
                src={storeImg} 
                alt={item.store}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            ) : null}
            <S.ProfileAvatarPlaceholder 
              variant="store"
              style={{ display: storeImg ? "none" : "flex" }}
            >
              {item.store ? item.store.charAt(0).toUpperCase() : "?"}
            </S.ProfileAvatarPlaceholder>
            <S.ProfileDetails>
              <S.ProfileName>{item.store}</S.ProfileName>
              {item.storeEmail && <S.ProfileSubtext>{item.storeEmail}</S.ProfileSubtext>}
              {item.storePhone && <S.ProfileSubtext>{item.storePhone}</S.ProfileSubtext>}
            </S.ProfileDetails>
          </S.ProfileCard>
        );
      }
    },
    { title: "Sold Qty", key: "soldQty" },
    {
      title: "Total Sales",
      key: "totalSales",
      render: (item: any) =>
        Helper.formatCurrency(item.totalSales, currencySettings),
    },
    {
      title: "Stock Status",
      key: "status",
      render: (item: any) => (
        <S.StatusPill
          bg={item.status === "In Stock" ? "#f6ffed" : "#fff1f0"}
          color={item.status === "In Stock" ? "#52c41a" : "#ff4d4f"}
        >
          {item.status}
        </S.StatusPill>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (item: any) => (
        <button
          onClick={() => {
            showModal(
              <div
                style={{ height: "90vh", overflowY: "auto", padding: "20px" }}
              >
                <CreateProductForm data={{ productData: { id: item.id } }} />
              </div>,
              { width: "95%" },
            );
          }}
          style={{
            padding: "6px 12px",
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
            background: "#fff",
            fontSize: "12px",
            cursor: "pointer",
            color: "#6366f1",
            fontWeight: 500,
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <S.Container>
      <S.Header>
        <S.Title>Item Report</S.Title>
        <S.SubTitle>
          Track product-level performance and stock status.
        </S.SubTitle>
      </S.Header>

      <S.StatsGrid>
        {stats.map((stat, i) => (
          <S.StatCard key={i} color={stat.color}>
            <S.StatIcon color={stat.color} bgColor={stat.bgColor}>
              <stat.Icon size={24} />
            </S.StatIcon>
            <S.StatInfo>
              <S.StatValue>{stat.value}</S.StatValue>
              <S.StatLabel>{stat.label}</S.StatLabel>
            </S.StatInfo>
          </S.StatCard>
        ))}
      </S.StatsGrid>

      <S.FilterSection>
        <S.FilterItem>
          <S.Label>Search Items</S.Label>
          <S.Input
            type="text"
            placeholder="Search product name..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Module</S.Label>
          <S.Select
            value={filters.moduleId}
            onChange={(e) => handleFilterChange("moduleId", e.target.value)}
          >
            <option value="">All Modules</option>
            {modules?.map((m: any) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Zone</S.Label>
          <S.Select
            value={filters.zoneId}
            onChange={(e) => handleFilterChange("zoneId", e.target.value)}
          >
            <option value="">All Zones</option>
            {zones?.map((z: any) => (
              <option key={z.id} value={z.id}>{z.name}</option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Store</S.Label>
          <S.Select
            value={filters.storeId}
            onChange={(e) => handleFilterChange("storeId", e.target.value)}
            disabled={!filters.moduleId}
          >
            <option value="">
              {filters.moduleId ? "All Stores" : "Select a module first"}
            </option>
            {stores?.map((s: any) => (
              <option key={s.id} value={s.id}>{s.storeName || s.name}</option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Time Frame</S.Label>
          <S.Select
            value={filters.dateRange}
            onChange={(e) => handleFilterChange("dateRange", e.target.value)}
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
            <option value="custom">Custom Time</option>
          </S.Select>
        </S.FilterItem>
        {filters.dateRange === "custom" && (
          <>
            <S.FilterItem>
              <S.Label>Start Date</S.Label>
              <S.Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </S.FilterItem>
            <S.FilterItem>
              <S.Label>End Date</S.Label>
              <S.Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </S.FilterItem>
          </>
        )}
      </S.FilterSection>

      <S.TableWrapper>
        <S.TableHeader>
          <S.TableTitle>Item Sales Data ({totalItems})</S.TableTitle>
          <S.ExportButton onClick={exportExcel} disabled={exporting}>
            <Download size={16} /> {exporting ? "Exporting..." : "Export Excel"}
          </S.ExportButton>
        </S.TableHeader>
        <Table
          columns={columns}
          data={data}
          loading={loading}
          totalPages={totalPages}
          onPageChange={setPage}
          pageControlled={page}
        />
      </S.TableWrapper>
    </S.Container>
  );
};

export default ItemReport;
